import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Calendar, Clock, MapPin, Video, X, Check, Loader2 } from 'lucide-react';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import api from '../lib/api';
import SuccessModal from './SuccessModal';

const DAYS_OF_WEEK = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];

export const AppointmentModal = ({ isOpen, onClose, destination, visaType }) => {
  const [loading, setLoading] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [reference, setReference] = useState('');
  const [availableSlots, setAvailableSlots] = useState([]);
  const [saturdaySlots, setSaturdaySlots] = useState([]);
  
  const [formData, setFormData] = useState({
    client_name: '',
    client_email: '',
    client_phone: '',
    preferred_date: '',
    preferred_time: '',
    meeting_type: '',
    notes: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchSlots();
    }
  }, [isOpen]);

  const fetchSlots = async () => {
    try {
      const res = await api.get('/appointments/slots');
      setAvailableSlots(res.data.slots);
      setSaturdaySlots(res.data.saturday_slots);
    } catch (error) {
      console.error('Error fetching slots:', error);
    }
  };

  const getMinDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const getAvailableSlotsForDate = () => {
    if (!formData.preferred_date) return [];
    const date = new Date(formData.preferred_date);
    const dayOfWeek = date.getDay();
    
    // Sunday - closed
    if (dayOfWeek === 0) return [];
    
    // Saturday - limited hours
    if (dayOfWeek === 6) return saturdaySlots;
    
    // Weekdays
    return availableSlots;
  };

  const getDayName = () => {
    if (!formData.preferred_date) return '';
    const date = new Date(formData.preferred_date);
    return DAYS_OF_WEEK[date.getDay()];
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.preferred_date || !formData.preferred_time || !formData.meeting_type) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }

    setLoading(true);
    try {
      const res = await api.post('/appointments', {
        ...formData,
        destination: destination,
        visa_type: visaType
      });
      
      setReference(res.data.reference);
      onClose();
      setShowSuccess(true);
      
      // Reset form
      setFormData({
        client_name: '',
        client_email: '',
        client_phone: '',
        preferred_date: '',
        preferred_time: '',
        meeting_type: '',
        notes: ''
      });
    } catch (error) {
      toast.error("Erreur lors de l'envoi de la demande");
    } finally {
      setLoading(false);
    }
  };

  const slotsForSelectedDate = getAvailableSlotsForDate();
  const isSunday = formData.preferred_date && new Date(formData.preferred_date).getDay() === 0;

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-[#020617]/90 backdrop-blur-sm"
              onClick={onClose}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />

            {/* Modal */}
            <motion.div
              className="relative bg-gradient-to-br from-[#0F172A] to-[#1E293B] rounded-2xl p-6 max-w-lg w-full max-h-[90vh] overflow-y-auto border border-white/10 shadow-2xl"
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
            >
              {/* Close button */}
              <button
                onClick={onClose}
                className="absolute top-4 right-4 text-[#94A3B8] hover:text-white transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              {/* Header */}
              <div className="mb-6">
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-5 h-5 text-[#D4AF37]" />
                  <h2 className="text-lg font-bold text-white">Prendre un Rendez-vous</h2>
                </div>
                <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/20 rounded-lg p-3">
                  <p className="text-[#D4AF37] text-xs font-medium">{visaType}</p>
                  <p className="text-[#94A3B8] text-xs">{destination}</p>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-3">
                {/* Personal Info */}
                <div>
                  <Label className="text-[#94A3B8] text-xs">Nom Complet *</Label>
                  <Input
                    value={formData.client_name}
                    onChange={(e) => setFormData({...formData, client_name: e.target.value})}
                    required
                    placeholder="Votre nom complet"
                    className="bg-white/5 border-white/10 text-white text-xs h-9"
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Email *</Label>
                    <Input
                      type="email"
                      value={formData.client_email}
                      onChange={(e) => setFormData({...formData, client_email: e.target.value})}
                      required
                      placeholder="votre@email.com"
                      className="bg-white/5 border-white/10 text-white text-xs h-9"
                    />
                  </div>
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Téléphone *</Label>
                    <Input
                      value={formData.client_phone}
                      onChange={(e) => setFormData({...formData, client_phone: e.target.value})}
                      required
                      placeholder="+243..."
                      className="bg-white/5 border-white/10 text-white text-xs h-9"
                    />
                  </div>
                </div>

                {/* Date Selection */}
                <div>
                  <Label className="text-[#94A3B8] text-xs">Date souhaitée *</Label>
                  <Input
                    type="date"
                    value={formData.preferred_date}
                    onChange={(e) => setFormData({...formData, preferred_date: e.target.value, preferred_time: ''})}
                    required
                    min={getMinDate()}
                    className="bg-white/5 border-white/10 text-white text-xs h-9"
                  />
                  {formData.preferred_date && (
                    <p className="text-[#94A3B8] text-xs mt-1">{getDayName()}</p>
                  )}
                </div>

                {/* Time Selection */}
                {formData.preferred_date && (
                  <div>
                    <Label className="text-[#94A3B8] text-xs">Heure souhaitée *</Label>
                    {isSunday ? (
                      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-3 mt-1">
                        <p className="text-red-400 text-xs">Fermé le dimanche. Veuillez choisir un autre jour.</p>
                      </div>
                    ) : slotsForSelectedDate.length > 0 ? (
                      <div className="grid grid-cols-4 gap-2 mt-2">
                        {slotsForSelectedDate.map((slot) => (
                          <button
                            key={slot}
                            type="button"
                            onClick={() => setFormData({...formData, preferred_time: slot})}
                            className={`py-2 px-3 rounded-lg text-xs font-medium transition-all ${
                              formData.preferred_time === slot
                                ? 'bg-[#D4AF37] text-[#020617]'
                                : 'bg-white/5 border border-white/10 text-[#94A3B8] hover:border-[#D4AF37]/50'
                            }`}
                          >
                            {slot}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                )}

                {/* Meeting Type */}
                <div>
                  <Label className="text-[#94A3B8] text-xs">Type de rendez-vous *</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, meeting_type: 'presential'})}
                      className={`p-3 rounded-lg border transition-all flex flex-col items-center gap-2 ${
                        formData.meeting_type === 'presential'
                          ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37]'
                          : 'bg-white/5 border-white/10 text-[#94A3B8] hover:border-white/30'
                      }`}
                    >
                      <MapPin className="w-5 h-5" />
                      <span className="text-xs font-medium">En présentiel</span>
                      <span className="text-[10px] opacity-70">Bureau Kinshasa</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({...formData, meeting_type: 'online'})}
                      className={`p-3 rounded-lg border transition-all flex flex-col items-center gap-2 ${
                        formData.meeting_type === 'online'
                          ? 'bg-[#D4AF37]/10 border-[#D4AF37] text-[#D4AF37]'
                          : 'bg-white/5 border-white/10 text-[#94A3B8] hover:border-white/30'
                      }`}
                    >
                      <Video className="w-5 h-5" />
                      <span className="text-xs font-medium">En ligne</span>
                      <span className="text-[10px] opacity-70">Appel vidéo</span>
                    </button>
                  </div>
                </div>

                {/* Notes */}
                <div>
                  <Label className="text-[#94A3B8] text-xs">Notes (optionnel)</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Informations supplémentaires..."
                    rows={2}
                    className="bg-white/5 border-white/10 text-white text-xs resize-none"
                  />
                </div>

                {/* Info box */}
                <div className="bg-sky-500/10 border border-sky-500/20 rounded-lg p-3">
                  <p className="text-sky-300 text-xs">
                    Un agent confirmera votre rendez-vous par WhatsApp et email. Vous recevrez un rappel 2h avant le RDV.
                  </p>
                </div>

                {/* Submit */}
                <div className="flex gap-3 pt-2">
                  <button
                    type="button"
                    onClick={onClose}
                    className="flex-1 btn-glass py-2.5 text-white text-xs"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={loading || isSunday || !formData.preferred_time || !formData.meeting_type}
                    className="flex-1 btn-gold py-2.5 text-xs flex items-center justify-center gap-2 disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Envoi...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4" />
                        Confirmer le RDV
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <SuccessModal
        isOpen={showSuccess}
        onClose={() => setShowSuccess(false)}
        title="Demande de RDV envoyée !"
        message={`Votre demande de rendez-vous (Réf: ${reference}) a été transmise. Un agent vous contactera pour confirmer le créneau.`}
      />
    </>
  );
};

export default AppointmentModal;
